import React, { useState, useContext, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { RestaurantConfirmedButton } from './RestaurantConfirmedButton';
import DoneIcon from '@mui/icons-material/Done';
import { typography } from '../../../../theme';
import { OrderType } from '../../../../types';
import dayjs from '../../../../utils/dayjs';
import { AddressSelectionContext } from '../../AddressSelectionProvider';
import {
  DeliveryFeePrice,
  IconActive,
  RedirectedRestaurantText,
  RedirectedRestaurantTitle,
  RestaurantRowButton,
  RestaurantRowProps,
  RestaurantRowTime,
  RestaurantRowWrapper,
  RestaurantTitle,
  ServiceFeePrice,
} from './RestaurantRow';
import { useDeliveryCampaign } from './hooks/useDeliveryCampaign';
import { useShownServiceFee } from './hooks/useShownServiceFee';
import { Currency } from '../../../shoppingCart/components/orderItem/Currency';
import { getCoordinates } from '../../helpers';
import { useAddress } from '../../useAddress';
import {
  calculatePreorderDeliveryFeeRange,
  getDeliveryZoneStaticFeeForDate,
} from '../../../../clients/shops-api';
import { trackInsightsException } from '../../../../utils/telemetry/appinsights';
import { checkRestaurantAvailableForPreorder } from './preOrderHelpers';
import { colors } from '../../../../theme/color';
import { useServiceFeeViewEvent } from '../../../../hooks/useServiceFeeViewEvent';
import { AnalyticsServiceFeeLocation } from '../../../../utils/analytics/analytics';

type DeliveryFeeRange = {
  min: number;
  max: number;
};

const PreOrderRestaurantRow: React.FC<RestaurantRowProps> = ({ active, restaurant }) => {
  const {
    displayName,
    deliveryFee = 8.9,
    restaurantId,
    deliveryRedirectionRestaurant,
    preorderDeliveryFee,
    preorderMinimumDeliveryFee,
    preorderMaximumDeliveryFee,
    serviceFee,
  } = restaurant;

  const [deliveryFeeRange, setDeliveryFeeRange] = useState<DeliveryFeeRange>({
    min: preorderMinimumDeliveryFee || preorderDeliveryFee || deliveryFee,
    max: preorderMaximumDeliveryFee || preorderDeliveryFee || deliveryFee,
  });

  const shownServiceFee = useShownServiceFee(serviceFee);

  const address = useAddress();
  const { orderType, setRestaurantId, preorderTime } = useContext(AddressSelectionContext);

  const { campaignDeliveryFee, useCampaignPrice } = useDeliveryCampaign({
    restaurant,
    isPreorder: true,
  });

  const preordersPossible = useMemo(() => {
    if (!preorderTime) return false;
    return checkRestaurantAvailableForPreorder(restaurant, orderType, preorderTime);
  }, [preorderTime, orderType, restaurant]);

  // Both branches of the price row below render the fee under the same condition, so a
  // single impression here covers delivery and pickup alike. Unlike RestaurantRow this
  // row is not gated on the restaurant being open.
  useServiceFeeViewEvent({
    isRendered: true,
    location: AnalyticsServiceFeeLocation.AddressSelection,
    serviceFee: shownServiceFee,
    orderType,
    restaurantId,
  });

  const chooseRestaurant = (): void => {
    if (!preordersPossible) return;

    setRestaurantId(restaurantId || -1);
  };

  const calculateDeliveryPriceRange = useCallback(
    async (time: dayjs.Dayjs | null, day?: dayjs.Dayjs): Promise<void> => {
      if (!address) return;

      try {
        const { lat, lng } = await getCoordinates(address);

        if (time) {
          const currentDeliveryFee = await getDeliveryZoneStaticFeeForDate(
            restaurantId || -1,
            time,
            lat,
            lng
          );

          setDeliveryFeeRange({ min: currentDeliveryFee, max: currentDeliveryFee });
        } else {
          const { minimumPreorderDeliveryFee, maximumPreorderDeliveryFee } =
            await calculatePreorderDeliveryFeeRange(restaurantId || -1, lat, lng, day || dayjs());

          setDeliveryFeeRange({
            min: minimumPreorderDeliveryFee || deliveryFee,
            max: maximumPreorderDeliveryFee || deliveryFee,
          });
        }
      } catch (err) {
        setDeliveryFeeRange({ min: deliveryFee, max: deliveryFee });
        trackInsightsException('FRONTEND_DELIVERY_PRICE_RANGE_ERROR', err);
      }
    },
    [address, deliveryFee, restaurantId]
  );

  useEffect(() => {
    if (orderType !== OrderType.DELIVERY) {
      return;
    }
    const run = (): void => {
      void calculateDeliveryPriceRange(preorderTime);
    };
    queueMicrotask(run);
  }, [calculateDeliveryPriceRange, preorderTime, orderType]);

  const restaurantIsRedirected = orderType === OrderType.DELIVERY && deliveryRedirectionRestaurant;

  return (
    <RestaurantRowWrapper active={active && preordersPossible} online={preordersPossible}>
      <RestaurantRowButton
        aria-selected={active && preordersPossible}
        onClick={chooseRestaurant}
        data-testid={`preorder-restaurant-row-${restaurantId}`}
      >
        <RowUpper>
          <div>
            <RestaurantTitle redirected={Boolean(active && restaurantIsRedirected)}>
              {displayName}
              {active && restaurantIsRedirected && (
                <span data-testid={`restaurant-not-delivering-${restaurantId}`}>
                  {' '}
                  ei kuljeta toistaiseksi
                </span>
              )}
            </RestaurantTitle>
            {orderType === OrderType.DELIVERY ? (
              <PriceRow>
                {shownServiceFee > 0 && (
                  <ServiceFeePrice data-testid="service-fee">
                    <span>Palvelumaksu </span>
                    <Currency>{shownServiceFee}</Currency>
                  </ServiceFeePrice>
                )}
                <PreOrderDeliveryFee>
                  <span>
                    {useCampaignPrice ? 'Kanta-asiakkaiden kuljetushinta nyt' : 'Kuljetus:'}
                  </span>
                  {useCampaignPrice ? (
                    <DeliveryFeePrice color={colors.orange.base} isPreorder={true}>
                      <Currency>{campaignDeliveryFee}</Currency>
                    </DeliveryFeePrice>
                  ) : (
                    <DeliveryFeePrice color={colors.textDefault} isPreorder={true}>
                      <Currency>{deliveryFeeRange.min}</Currency>
                      {deliveryFeeRange.min < deliveryFeeRange.max && (
                        <>
                          {' — '}
                          <Currency>{deliveryFeeRange.max}</Currency>
                        </>
                      )}
                    </DeliveryFeePrice>
                  )}
                </PreOrderDeliveryFee>
              </PriceRow>
            ) : (
              shownServiceFee > 0 && (
                <PriceRow>
                  <ServiceFeePrice data-testid="service-fee">
                    <span>Palvelumaksu </span>
                    <Currency>{shownServiceFee}</Currency>
                  </ServiceFeePrice>
                </PriceRow>
              )
            )}
          </div>
          <IconActive active={active && preordersPossible} aria-hidden="true">
            <DoneIcon htmlColor="white" fontSize="inherit" />
          </IconActive>
          {active && restaurantIsRedirected && (
            <RedirectedRestaurantText>
              {deliveryRedirectionRestaurant && (
                <RedirectedRestaurantTitle>
                  {deliveryRedirectionRestaurant.displayName}
                </RedirectedRestaurantTitle>
              )}
              <span> valmistaa ja kuljettaa tilauksesi.</span>
            </RedirectedRestaurantText>
          )}
        </RowUpper>
      </RestaurantRowButton>
      {active && preordersPossible && (
        <>
          <RestaurantConfirmedButton disabled={!active} />
        </>
      )}
      {!preordersPossible && (
        <BottomRowOpeningTime>
          <RestaurantRowTime
            text="Ravintola ei ota vastaan ennakkotilauksia valitulle ajalle."
            restaurantDisplayName={displayName}
            showLinkToOpeningHours={!preordersPossible}
          />
        </BottomRowOpeningTime>
      )}
    </RestaurantRowWrapper>
  );
};

type PreorderProps = {
  preOrderTimes: dayjs.Dayjs[];
};

export const PreorderTimeSelection: React.FC<PreorderProps> = ({ preOrderTimes, ...props }) => {
  const { setPreorderTime, preorderTime } = useContext(AddressSelectionContext);

  const [selectedDay, setSelectedDay] = useState<dayjs.Dayjs>(
    preorderTime && preOrderTimes.some((day) => day.isSame(dayjs(preorderTime), 'day')) // Check that selected preorder time is in list
      ? dayjs(preorderTime)
      : dayjs(preOrderTimes[0])
  );
  const [selectedTime, setSelectedTime] = useState<dayjs.Dayjs | undefined>(
    preorderTime ? dayjs(preorderTime) : undefined
  );

  const handleSelectedDay = useCallback(
    (day: string) => {
      setSelectedDay(dayjs(day));
      setSelectedTime(undefined);
      setPreorderTime(null);
    },
    [setSelectedDay, setSelectedTime, setPreorderTime]
  );

  const handleSelectedTime = useCallback(
    (day: string) => {
      const time = dayjs(day);
      setSelectedDay(time);
      setSelectedTime(time);
      setPreorderTime(time);
    },
    [setSelectedDay, setSelectedTime, setPreorderTime]
  );

  return (
    <PreOrderList {...props}>
      <StyledPreorderTimeHeading as="h4">Päivä ja aika</StyledPreorderTimeHeading>
      <div>
        <SelectPreOrder
          data-testid="preorderDate-select"
          name="days"
          value={selectedDay.format('YYYY-MM-DD')}
          onChange={(e) => {
            handleSelectedDay(e.target.value);
          }}
        >
          <option disabled value="">
            Valitse päivä
          </option>
          {renderPreOrderDaysOptions(preOrderTimes)}
        </SelectPreOrder>
        <SelectPreOrder
          data-testid="preorderTime-select"
          name="times"
          value={selectedTime?.toISOString() || ''}
          onChange={(e) => {
            handleSelectedTime(e.target.value);
          }}
        >
          <option disabled value="">
            Valitse aika
          </option>
          {renderPreOrderTimesOptions(selectedDay, preOrderTimes)}
        </SelectPreOrder>
      </div>
    </PreOrderList>
  );
};

const renderPreOrderDaysOptions = (days: dayjs.Dayjs[]): React.ReactNode =>
  days
    .filter((day, index) => {
      const firstIndex = days.findIndex((otherDay) => otherDay.isSame(day, 'day'));
      return index === firstIndex;
    })
    .map((day) => (
      <option
        data-testid={`preorder-day-${day.format('YYYY-MM-DD')}`}
        key={day.format('L')}
        value={day.format('YYYY-MM-DD')}
      >
        {day.tz('Europe/Helsinki').format('L, dddd')}
      </option>
    ));

const renderPreOrderTimesOptions = (
  day: dayjs.Dayjs,
  preOrderTimes: dayjs.Dayjs[]
): React.ReactNode =>
  preOrderTimes
    .filter((time) => time.isSame(day, 'day'))
    .map((time) => (
      <option
        data-testid={`preorder-time-${time.toISOString()}`}
        key={time.format('H.mm')}
        value={time.toISOString()}
      >
        {time.tz('Europe/Helsinki').format('HH.mm')}
      </option>
    ));

const RowUpper = styled.div`
  position: relative;
  margin-bottom: 5px;
`;

const PriceRow = styled.div`
  margin-top: 5px;
`;

const RowBottomPreOrder = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 16px;
`;

const BottomRowOpeningTime = styled(RowBottomPreOrder)`
  margin: 5px 0;
`;

const PreOrderDeliveryFee = styled.span`
  display: block;
  line-height: 125%;

  b {
    margin-left: 5px;
  }
`;

const StyledPreorderTimeHeading = styled(typography.HeadingTiny)`
  margin: 16px 0 8px 0;
`;

const PreOrderList = styled.div`
  display: block;
  width: 100%;
  margin-top: 10px;
`;

const SelectPreOrder = styled.select`
  ${typography.textStyle}

  &:first-child {
    margin-bottom: 8px;
  }

  &:last-child {
    margin-bottom: 0;
  }

  color: ${colors.textDefault};
  width: 100%;
  max-width: 100%;
  margin: 0;
  height: 2.2rem;
  border-radius: 4px;
  background: ${colors.white};
  padding: 0 0 0 10px;
  font-size: 0.875rem;
  font-weight: 400;
  border-color: ${colors.borderDefault};
`;

export default PreOrderRestaurantRow;
